import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchManagementReports, addManagementReport, deleteManagementReport, updateManagementReport } from '../../services/api';
import { FaSearch, FaEdit, FaTrash, FaSortUp, FaSortDown } from 'react-icons/fa';
import ManagementReportsModal from './ManagementReportsModal';
import './ManagementReports.css';
import { Report, createEmptyReport, requiredReportFields } from '../../models/Report';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const initialForm: Report = createEmptyReport();

const ManagementReports: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [form, setForm] = useState<Report>(initialForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [touched, setTouched] = useState<{[key: string]: boolean}>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [editingReport, setEditingReport] = useState<Report | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof Report>('displayname');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const isGuid = (value: string) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value);

  const validateForm = () => {
    const missing = requiredReportFields.filter(field => !form[field]);
    const invalidGuids = [];
    if (form.workspaceid && !isGuid(form.workspaceid)) invalidGuids.push('workspaceid');
    if (form.reportid && !isGuid(form.reportid)) invalidGuids.push('reportid');
    return { missing, invalidGuids };
  };

  const loadReports = () => {
    setLoading(true);
    setError(null);
    fetchManagementReports()
      .then(data => {
        setReports(data);
        setLoading(false);
      })
      .catch(err => {
        setError(t('management.failedToLoadReports'));
        setLoading(false);
      });
  };

  useEffect(() => {
    loadReports();
  }, []);

  // Sorting logic
  const handleSort = (column: keyof Report) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filtered, sorted, and paginated data
  const filtered = reports.filter(r =>
    r.displayname.toLowerCase().includes(search.toLowerCase()) ||
    r.category.toLowerCase().includes(search.toLowerCase()) ||
    r.workspacename.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const aValue = a[sortColumn] ?? '';
    const bValue = b[sortColumn] ?? '';
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / pageSize) || 1;
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleOpenModal = () => {
    setModalMode('add');
    setForm(initialForm);
    setFormError(null);
    setTouched({});
    setShowModal(true);
  };

  const handleEditClick = (report: Report) => {
    setModalMode('edit');
    setEditingReport(report);
    const normalizedType = ((): Report['type'] => {
      const v = (report as any).type;
      if (v === 'Paginated Report' || v === 'Dashboard' || v === 'Report') return v;
      if (typeof v === 'string') {
        const lower = v.toLowerCase();
        if (lower === 'paginated report' || lower === 'paginated_report' || lower === 'paginated') return 'Paginated Report';
        if (lower === 'dashboard') return 'Dashboard';
        if (lower === 'report') return 'Report';
      }
      return 'Report';
    })();
    // Ensure refreshFrequency is a number (handle legacy string values)
    const normalizedRefreshFrequency = typeof report.refreshFrequency === 'string' 
      ? 0 // If it's a string (legacy data), default to 0 (never)
      : (report.refreshFrequency ?? 0);
    setForm({
      ...createEmptyReport(),
      ...report,
      type: normalizedType,
      refreshFrequency: normalizedRefreshFrequency,
    });
    setFormError(null);
    setTouched({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormError(null);
    setEditingReport(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Convert refreshFrequency to number if it's that field
    // The value might already be a number from the synthetic event
    const formValue = name === 'refreshFrequency' 
      ? (typeof value === 'number' ? value : parseInt(String(value), 10) || 0)
      : value;
    setForm({ ...form, [name]: formValue });
    setTouched({ ...touched, [name]: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    setTouched(requiredReportFields.reduce((acc, f) => ({ ...acc, [f]: true }), {}));
    const { missing, invalidGuids } = validateForm();
    if (missing.length > 0) {
      setFormError(t('management.fillRequiredFields'));
      setSaving(false);
      return;
    }
    if (invalidGuids.length > 0) {
      setFormError(t('management.invalidGuids'));
      setTouched(t => ({ ...t, workspaceid: true, reportid: true }));
      setSaving(false);
      return;
    }
    try {
      if (modalMode === 'add') {
        await addManagementReport(form);
      } else {
        if (!editingReport?.id) {
          setFormError(t('management.missingReportId'));
          setSaving(false);
          return;
        }
        await updateManagementReport(editingReport.id, form);
      }
      setShowModal(false);
      setSaving(false);
      setEditingReport(null);
      loadReports();
    } catch (err) {
      setFormError(modalMode === 'add' ? t('management.failedToAddReport') : t('management.failedToUpdateReport'));
      setSaving(false);
    }
  };

  const handleDeleteClick = (report: Report) => {
    setSelectedReport(report);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedReport(null);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedReport?.id) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteManagementReport(selectedReport.id);
      setShowDeleteModal(false);
      setSelectedReport(null);
      setDeleting(false);
      loadReports();
    } catch (err) {
      setDeleteError(t('management.failedToDeleteReport'));
      setDeleting(false);
    }
  };

  return (
    <div className="management-reports">
      <div className="management-reports__header">
        <div className="management-reports__search-container">
          <span className="management-reports__search-icon">{FaSearch({})}</span>
          <input
            type="text"
            placeholder={t('common.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="management-reports__search-input"
          />
        </div>
        <button
          onClick={handleOpenModal}
          className="management-reports__add-button"
        >
          + {t('management.addReport')}
        </button>
      </div>
      <ManagementReportsModal
        isOpen={showModal}
        mode={modalMode}
        form={form}
        saving={saving}
        formError={formError}
        touched={touched}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        onFormChange={handleFormChange}
        isGuid={isGuid}
      />
      <div className="management-reports__table-container">
        {loading ? (
          <div className="management-reports__loading">{t('common.loading')}</div>
        ) : error ? (
          <div className="management-reports__error">{error}</div>
        ) : (
          <div className="management-reports__table-scroll">
            <table className="management-reports__table">
              <thead className="management-reports__thead">
                <tr>
                  <th className="management-reports__th" onClick={() => handleSort('displayname')} style={{ cursor: 'pointer' }}>
                    {t('management.name')} {sortColumn === 'displayname' && (sortDirection === 'asc' ? FaSortUp({style:{fontSize:12}}) : FaSortDown({style:{fontSize:12}}))}
                  </th>
                  <th className="management-reports__th" onClick={() => handleSort('reportid')} style={{ cursor: 'pointer' }}>
                    {t('management.reportId')} {sortColumn === 'reportid' && (sortDirection === 'asc' ? FaSortUp({style:{fontSize:12}}) : FaSortDown({style:{fontSize:12}}))}
                  </th>
                  <th className="management-reports__th" onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                    {t('management.category')} {sortColumn === 'category' && (sortDirection === 'asc' ? FaSortUp({style:{fontSize:12}}) : FaSortDown({style:{fontSize:12}}))}
                  </th>
                  <th className="management-reports__th" onClick={() => handleSort('workspacename')} style={{ cursor: 'pointer' }}>
                    {t('management.workspace')} {sortColumn === 'workspacename' && (sortDirection === 'asc' ? FaSortUp({style:{fontSize:12}}) : FaSortDown({style:{fontSize:12}}))}
                  </th>
                  <th className="management-reports__th" style={{textAlign: 'center'}}>{t('management.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((r, i) => (
                  <tr key={r.id || i} className="management-reports__tr">
                    <td className="management-reports__td">{r.displayname}</td>
                    <td className="management-reports__td">{r.reportid}</td>
                    <td className="management-reports__td">{r.category}</td>
                    <td className="management-reports__td">{r.workspacename}</td>
                    <td className="management-reports__td management-reports__td--actions">
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 8, color: '#1565c0' }} title={t('common.edit')} onClick={() => handleEditClick(r)}>
                        {FaEdit({})}
                      </button>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d32f2f' }} title={t('common.delete')} onClick={() => handleDeleteClick(r)}>
                        {FaTrash({})}
                      </button>
                    </td>
                  </tr>
                ))}
                {paginated.length === 0 && (
                  <tr><td colSpan={5} className="management-reports__empty-row">{t('management.noReportsFound')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="management-reports__pagination">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          className="management-reports__page-btn"
        >
          {'<'}
        </button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => setPage(i + 1)}
            className={`management-reports__page-num${page === i + 1 ? ' management-reports__page-num--active' : ''}`}
          >
            {i + 1}
          </button>
        ))}
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          className="management-reports__page-btn"
        >
          {'>'}
        </button>
        <select
          value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          className="management-reports__page-size-select"
        >
          {PAGE_SIZE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="management-reports__delete-overlay">
          <div className="management-reports__delete-container">
            <h2 className="management-reports__delete-title">{t('management.deleteReport')}</h2>
            <div className="management-reports__delete-message">
              {t('management.deleteConfirm')} <b>{selectedReport?.displayname}</b>?
            </div>
            {deleteError && <div className="management-reports__delete-error">{deleteError}</div>}
            <div className="management-reports__delete-actions">
              <button onClick={handleDeleteCancel} className="management-reports__delete-btn management-reports__delete-btn--cancel">{t('common.cancel')}</button>
              <button onClick={handleDeleteConfirm} disabled={deleting} className="management-reports__delete-btn management-reports__delete-btn--confirm">{deleting ? t('management.deleting') : t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementReports; 